import { BjThemeColors } from '@type/bj_theme';
import { BadgePill, Tooltip } from '@components/common';
import { useTranslation } from 'next-i18next';
import {
  applicantStatusIdObj,
  ApplicantBoardPathStatus,
  ApplicantBoardQueryFilterStatus,
  ApplicantStatus,
} from '@components/templates/employer/applicant-list/functions/statusConstants';
import { StatusChangeActorEnum } from '@type/v1-api-types';
import { clsx } from 'clsx';

const isStatusRedundantForContext = (statusId?: number, statusContext?: string | number) => {
  if (!statusId || statusContext === undefined || statusContext === null) return false;

  if (typeof statusContext === 'number') {
    return statusId === statusContext;
  }

  switch (statusContext) {
    case 'rejected':
    case ApplicantBoardPathStatus.REJECTED:
    case ApplicantBoardQueryFilterStatus.BSF_REJECTED:
      return statusId === ApplicantStatus.REJECTED;
    case 'applicants':
    case ApplicantBoardQueryFilterStatus.BSF_UNSELECTED:
      return statusId === ApplicantStatus.NEW || statusId === ApplicantStatus.VIEWED;
    case 'shortlist':
    case ApplicantBoardPathStatus.QUALIFIED:
      return statusId === ApplicantStatus.SHORTLIST;
    case 'interview':
    case ApplicantBoardPathStatus.EXTENDED_SELECTED:
    case ApplicantBoardQueryFilterStatus.BSF_INTERVIEW:
      return statusId === ApplicantStatus.INTERVIEW;
    case ApplicantBoardQueryFilterStatus.BSF_OFFER:
      return statusId === ApplicantStatus.OFFER;
    case ApplicantBoardQueryFilterStatus.BSF_HIRE:
      return statusId === ApplicantStatus.HIRED;
    default:
      return false;
  }
};

/**
 * @description Applicant badge pill component
 * @param statusId - the status id of the applicant (number for new, interview, shortlist, etc.)
 * @param statusChangeActor - source of shortlist: 'auto' | 'admin' | 'owner'
 * @param statusContext - current MC column or status page context; matching badges are hidden
 */
export const ApplicantBadgePill = ({
  statusId,
  statusChangeActor,
  statusContext,
}: {
  statusId?: number;
  statusChangeActor?: StatusChangeActorEnum | undefined;
  statusContext?: string | number;
}) => {
  // Find the status by statusId
  const status = statusId ? applicantStatusIdObj.find((item) => item.statusId === statusId) : undefined;
  const { t } = useTranslation('common');

  // If status is not found, return null
  if (!status) return null;

  if (isStatusRedundantForContext(statusId, statusContext)) return null;

  const isShortlist = statusId === ApplicantStatus.SHORTLIST;

  const actor: StatusChangeActorEnum | undefined = statusChangeActor;

  const ringClasses = isShortlist
    ? (actor === StatusChangeActorEnum.AUTO
      ? 'ring-2 ring-secondary p-1 rounded-full'
      : actor === StatusChangeActorEnum.ADMIN
        ? 'ring-2 ring-warning/20 p-1 rounded-full'
        : '')
    : '';

  const sourceLabel =
    actor === StatusChangeActorEnum.AUTO
      ? t('shortlisted_source_bestie')
      : actor === StatusChangeActorEnum.ADMIN
        ? t('shortlisted_source_agent')
        : undefined;

  const pill = (
    <BadgePill
      color={status.color as BjThemeColors}
      size="xxs"
      className="max-w-24 !px-2 text-center font-medium leading-tight"
    >
      <span className="block text-wrap break-all text-pico">{t(status.label)}</span>
    </BadgePill>
  );

  if (sourceLabel) {
    return (
      <Tooltip
        tooltipText={sourceLabel}
        position="top"
        floating
        triggerContainerClass="inline-flex relative z-2 pointer-events-auto"
      >
        <span className={clsx('inline-flex', ringClasses)}>{pill}</span>
      </Tooltip>
    );
  }

  return (
    <span className={clsx('inline-flex', 'relative z-2 pointer-events-auto', ringClasses)}>
      {pill}
    </span>
  );
};
